/**
 * Device Intelligence Page
 *
 * Main page for device fingerprinting and fraud detection.
 * Shows fraud statistics, device list with filters, and IP lookup tool.
 * Migrated to Tailwind + shadcn/ui
 */

import React, { useState, useMemo } from 'react';
import {
  Fingerprint,
  Search,
  Filter,
  Shield,
  AlertTriangle,
  Wifi,
  Globe,
  Monitor,
  Smartphone,
  Bot,
  Eye,
  ChevronRight,
} from 'lucide-react';
import { useDevices, useDeviceStats } from '../../hooks';
import { useDebounce } from '../../hooks/useDebounce';
import FraudStats from './FraudStats';
import IPLookup from './IPLookup';
import DeviceRiskCard from './DeviceRiskCard';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Spinner } from '../ui/spinner';
import { cn } from '../../lib/utils';

const RISK_CONFIG = {
  low: { label: 'Low', variant: 'success' },
  medium: { label: 'Medium', variant: 'warning' },
  high: { label: 'High', variant: 'destructive' },
};

export default function DeviceIntelPage() {
  const [ipSearch, setIpSearch] = useState('');
  const [riskFilter, setRiskFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showIPLookup, setShowIPLookup] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);

  const debouncedIpSearch = useDebounce(ipSearch, 300);

  const filters = useMemo(() => ({
    ...(debouncedIpSearch && { ip_address: debouncedIpSearch }),
    ...(riskFilter && { risk_level: riskFilter }),
    limit: 50,
  }), [debouncedIpSearch, riskFilter]);

  const { data, isLoading, error } = useDevices(filters);
  const { data: stats } = useDeviceStats(30);

  const devices = data?.items || [];

  return (
    <div className="max-w-[1400px]">
      {/* Page Header */}
      <div className="flex justify-between items-start mb-6 gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <Fingerprint className="w-8 h-8 text-teal-500" />
          <div>
            <h1 className="text-2xl font-semibold text-grey-900 dark:text-grey-50 m-0">
              Device Intelligence
            </h1>
            <p className="text-sm text-grey-500 dark:text-grey-400 mt-1">
              Device fingerprinting and network fraud detection
            </p>
          </div>
        </div>
        <div className="flex gap-3 items-center">
          <Button onClick={() => setShowIPLookup(true)}>
            <Search size={16} />
            IP Lookup
          </Button>
        </div>
      </div>

      {/* Fraud Statistics */}
      <FraudStats stats={stats} />

      {/* Toolbar */}
      <div className="flex gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-[400px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey-400" />
          <Input
            type="text"
            placeholder="Search by IP address..."
            value={ipSearch}
            onChange={(e) => setIpSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button
          variant={showFilters ? 'default' : 'secondary'}
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter size={16} />
          Filters
        </Button>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="flex gap-3 p-4 mb-6 flex-wrap">
          <select
            className="px-3 py-2 bg-grey-50 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 rounded-md text-sm text-grey-900 dark:text-grey-50 min-w-[150px] cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            value={riskFilter}
            onChange={(e) => setRiskFilter(e.target.value)}
          >
            <option value="">All Risk Levels</option>
            <option value="low">Low Risk</option>
            <option value="medium">Medium Risk</option>
            <option value="high">High Risk</option>
          </select>
          {riskFilter && (
            <button
              className="px-3 py-2 bg-transparent border-none text-teal-500 text-sm cursor-pointer hover:text-teal-600"
              onClick={() => setRiskFilter('')}
            >
              Clear Filters
            </button>
          )}
        </Card>
      )}

      {/* Devices Table */}
      <Card className="overflow-hidden">
        {/* Table Header */}
        <div className="hidden md:grid grid-cols-[1.5fr_1fr_1fr_1fr_1.5fr_40px] gap-4 px-5 py-3 bg-grey-50 dark:bg-grey-800/50 border-b border-grey-200 dark:border-grey-700">
          <div className="text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider">IP Address</div>
          <div className="text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider">Risk Level</div>
          <div className="text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider">Fraud Score</div>
          <div className="text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider">Device</div>
          <div className="text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider">Flags</div>
          <div></div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-16 text-grey-400">
            <Spinner size="lg" />
            <div className="mt-3 text-sm">Loading device data...</div>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="flex flex-col items-center justify-center py-16 text-center text-red-500">
            <AlertTriangle size={32} />
            <div className="mt-3 text-sm">Failed to load device data</div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && devices.length === 0 && (
          <div className="py-16 text-center">
            <Fingerprint className="w-12 h-12 text-grey-300 dark:text-grey-600 mx-auto mb-4" />
            <h3 className="text-base font-medium text-grey-900 dark:text-grey-50 mb-2">
              No device scans yet
            </h3>
            <p className="text-sm text-grey-500 dark:text-grey-400">
              {ipSearch || riskFilter
                ? 'Try adjusting your filters'
                : 'Device fingerprints will appear here after analysis'}
            </p>
          </div>
        )}

        {/* Device Rows */}
        {!isLoading && !error && devices.map((device) => {
          const risk = RISK_CONFIG[device.risk_level] || RISK_CONFIG.low;
          const fraudLevel = device.fraud_score >= 85 ? 'high' : device.fraud_score >= 70 ? 'medium' : 'low';
          const DeviceIcon = device.device_type === 'mobile' ? Smartphone : Monitor;

          return (
            <div
              key={device.id}
              className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr_1fr_1fr_1.5fr_40px] gap-4 px-5 py-4 border-b border-grey-200 dark:border-grey-700 last:border-b-0 cursor-pointer transition-colors hover:bg-grey-50 dark:hover:bg-grey-800/50 items-center"
              onClick={() => setSelectedDevice(device)}
            >
              {/* IP Address */}
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-grey-900 dark:text-grey-50 font-mono">
                  {device.ip_address}
                </span>
                {device.city && device.country_code && (
                  <span className="text-xs text-grey-500 dark:text-grey-400 flex items-center gap-1">
                    <Globe size={12} />
                    {device.city}, {device.country_code}
                  </span>
                )}
              </div>

              {/* Risk Level */}
              <div>
                <Badge variant={risk.variant} className="inline-flex items-center gap-1">
                  <Shield size={12} />
                  {risk.label}
                </Badge>
              </div>

              {/* Fraud Score */}
              <div>
                <span className={cn(
                  'text-sm font-semibold',
                  fraudLevel === 'high' && 'text-red-500',
                  fraudLevel === 'medium' && 'text-orange-500',
                  fraudLevel === 'low' && 'text-green-500'
                )}>
                  {device.fraud_score || 0}
                </span>
              </div>

              {/* Device */}
              <div className="flex items-center gap-1.5 text-sm text-grey-500 dark:text-grey-400">
                <DeviceIcon size={14} />
                {device.browser || 'Unknown'} / {device.operating_system || 'Unknown'}
              </div>

              {/* Flags */}
              <div className="flex gap-2 flex-wrap">
                {device.is_vpn && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-500/10 text-orange-500 rounded text-xs font-medium">
                    <Eye size={10} /> VPN
                  </span>
                )}
                {device.is_tor && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-500/10 text-red-500 rounded text-xs font-medium">
                    <AlertTriangle size={10} /> Tor
                  </span>
                )}
                {device.is_bot && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-500/10 text-red-500 rounded text-xs font-medium">
                    <Bot size={10} /> Bot
                  </span>
                )}
                {device.is_proxy && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-500/10 text-orange-500 rounded text-xs font-medium">
                    <Wifi size={10} /> Proxy
                  </span>
                )}
              </div>

              {/* Chevron */}
              <div className="text-grey-400 hidden md:block">
                <ChevronRight size={16} />
              </div>
            </div>
          );
        })}
      </Card>

      {/* IP Lookup Modal */}
      {showIPLookup && (
        <IPLookup onClose={() => setShowIPLookup(false)} />
      )}

      {/* Device Detail Drawer */}
      {selectedDevice && (
        <DeviceRiskCard
          device={selectedDevice}
          onClose={() => setSelectedDevice(null)}
        />
      )}
    </div>
  );
}
